import autoComplete from '@tarekraafat/autocomplete.js';
import { debounce } from "./lib/debounce";

const MeasureList = {
  /**
   * Collects necessary elements.
   * @function _cacheElements
   * @private
   */
  _cacheElements: function() {
    this.measureFilter = document.querySelector('.measures-list__filter');
    this.measureSearchInput = this.measureFilter.querySelector('.measures-list__search-input');
    this.measureSearchButton = this.measureFilter.querySelector('.form-group button');
    this.fieldOffset = this.measureFilter.querySelector('.measures-list__filter__field-offset');
    this.fieldCategoryList = this.measureFilter.querySelector('.measures-list__filter__field-category-list');

    this.categoriesListToggle = this.measureFilter.querySelector('.categories-list__toggle button');
    this.closeOverlay = this.measureFilter.querySelector('.categories-overlay__close');
    this.categoryItems = this.measureFilter.querySelectorAll('input[type="checkbox"]');
    this.categoriesListClose = this.measureFilter.querySelector('.js-categories-list__close');
    this.totalAmountIndicator = this.categoriesListClose.querySelector('.total-amount-of-measures');
    this.categoriesListReset = this.measureFilter.querySelector('.categories-list__reset');

    this.measuresList = document.querySelector('.measures-list' );
    this.measuresListItems = this.measuresList.querySelectorAll('.measures-list__item');
    this.loadMore = document.querySelector('.js-measures-list__load-more');

    this.loadingIndicator = document.querySelector('.loading-indicator__container');

    this.autoCompleteInstance = null;
  },

  /**
   * Adds event listeners to various filter elements.
   * @function _initFilter
   * @private
   */
  _bindEvents: function() {
    MeasureList.measureSearchInput.addEventListener('input', debounce(() => {
      MeasureList._createListAutocomplete();
    }));

    MeasureList.measureSearchButton.addEventListener('click', () => {
      if(MeasureList.measureSearchInput.closest('.form-group').querySelector('ul li')) {
        const firstListItem = MeasureList.measureSearchInput.closest('.form-group').querySelector('ul li').innerText; //first ListItem in ul-List
        MeasureList.measureSearchInput.value = firstListItem;

        MeasureList.fieldOffset.value = 0;
        MeasureList._createCategoryList();
        MeasureList._submitFilter();
      }
    })

    MeasureList.measureSearchInput.addEventListener('selection', (event) => {
      // write selected value from autocomplete list into the input field
      const selection = event.detail.selection.value;
      MeasureList.measureSearchInput.value = selection;

      MeasureList.fieldOffset.value = 0;
      MeasureList._createCategoryList();
      MeasureList._submitFilter();
    });

    MeasureList.measureFilter.addEventListener('submit', (event) => {
      event.preventDefault();

      MeasureList.fieldOffset.value = 0;
      MeasureList._createCategoryList();
      MeasureList._submitFilter();
    });

    if (MeasureList.loadMore) {
      MeasureList.loadMore.addEventListener('click', (event) => {
        event.preventDefault();

        MeasureList.fieldOffset.value = MeasureList.measuresList.querySelectorAll('.measures-list__item').length;
        MeasureList._createCategoryList();
        MeasureList._submitFilter();
      });
    }

    MeasureList.categoriesListToggle.addEventListener('click', () => {
      MeasureList._openOverlay();
    });

    MeasureList.categoryItems.forEach((categoryItem) => {
      categoryItem.addEventListener('change', function()  {
        MeasureList.fieldOffset.value = 0;

        MeasureList._createCategoryList();
        MeasureList._submitFilter();
      });
    });

    MeasureList.closeOverlay.addEventListener('click', () => {
      MeasureList._closeOverlay();
    });

    MeasureList.categoriesListClose.addEventListener('click', (event) => {
      MeasureList._closeOverlay();
    });

    MeasureList.categoriesListReset.addEventListener('click', (event) => {
      event.preventDefault();

      MeasureList.fieldOffset.value = 0;
      MeasureList._resetCategories();
      MeasureList._submitFilter();
      MeasureList._closeOverlay();
    });
  },

  /**
   * Initializes the autocomplete instance with custom configuration.
   * @function _initAutoComplete
   * @private
   */
   _initAutoComplete: function() {
    MeasureList.autoCompleteInstance = new autoComplete({
      data: {
          src: []
      },
      resultItem: {
          highlight: true,
      },
      resultsList: {
        maxResults: 6
      }
    });
  },

  /**
   * Restores the position of the provided measures list item.
   * @function _restoreItemIndex
   * @private
   * @param {HTMLElement} item - the measures list item whose index should be restored
   */
  _restoreItemIndex: function(item) {
    if (!item.dataset.initialIndex) {
      return;
    }

    const items = Array.from(MeasureList.measuresListItems);
    const newIndex = parseInt(item.dataset.initialIndex, 10);

    if (newIndex + 1 === items.length) {
      MeasureList.measuresList.insertAdjacentElement('beforeend', item);
    } else {
      MeasureList.measuresList.insertBefore(item, items[newIndex + 1]);
    }

    item.removeAttribute('data-initial-index');
  },

  /**
   * Creates a list of suggested words depending on the input value.
   * @async
   * @function _createListAutocomplete
   * @private
   */
  _createListAutocomplete: function() {
    const keyword = MeasureList.measureSearchInput.value;
    const url = MeasureList.measureSearchInput.dataset.autocompleteUrl;
    const payload = {};

    payload[MeasureList.measureSearchInput.name] = keyword;

    fetch(url, {
      method: 'POST',
      body: new URLSearchParams(payload)
    })
    .then((response) => response.ok && response.json()).then((result) => {
      const responseData = result;

      // update list of autocomplete words and render list
      MeasureList.autoCompleteInstance.data.src = responseData;
      MeasureList.autoCompleteInstance.start();
    });
  },

  /**
   * Creates submittable form data from the filter and sends it to the filter's action.
   * Once an answer is received the content is added to the list.
   * @function _submitFilter
   * @private
   */
  _submitFilter: function() {
    const filterData = new FormData(MeasureList.measureFilter);

    MeasureList._setLoading(true);

    fetch(MeasureList.measureFilter.action, {
      method: 'POST',
      body: filterData
    }).then(response => response.ok && response.text()).then((result) => {
      const clearList = MeasureList.fieldOffset.value === '0';
      const dummyElement = document.createElement('div');

      dummyElement.innerHTML = result;

      const newListElements = dummyElement.querySelectorAll('li.measures-list__item');
      const totalAmountElement = dummyElement.querySelector('code');
      let totalAmount = 100000;

      if (totalAmountElement) {
        totalAmount = parseInt(totalAmountElement.textContent, 10)
        totalAmountElement.remove();
      }

      MeasureList._updateList(clearList, newListElements);

      if (totalAmountElement) {
        MeasureList._toggleLoadMoreButton(totalAmount);
        MeasureList.totalAmountIndicator.textContent = totalAmount;
      }
    }).finally(() => {
      MeasureList._setLoading(false);
    });
  },

  /**
   * Shows the button to load more if there are more items available
   * Hides the button to load more if there are no items available.
   * @function _toggleLoadMoreButton
   * @private
   * @param {Number} totalAmount - total amount of available measures list items
   */
  _toggleLoadMoreButton: function(totalAmount) {
    if (!MeasureList.loadMore) {
      return
    }

    if (MeasureList.measuresListItems.length === totalAmount) {
      MeasureList.loadMore.classList.add('hidden');
    } else {
      MeasureList.loadMore.classList.remove('hidden');
    }
  },

  /**
   * Creates a comma-separated list of all active category checkboxes and writes it to a hidden field.
   * @function _createCategoryList
   * @private
   */
  _createCategoryList: function() {
    let categoryList = [];

    MeasureList.categoryItems.forEach((category) => {
      if (category.checked) {
        categoryList.push(category.value)
      }
    });

    MeasureList.categoriesListToggle.dataset.activeCategories = categoryList.length;
    MeasureList.fieldCategoryList.value = categoryList.join(',');
  },

  /**
   * Clears the list if specified as such and update elements with the provided search result.
   * @function _updateList
   * @private
   * @param {Boolean} clearList - specifies if the old content of the list is removed before adding the new content
   * @param {NodeList} newElements - list of elements that should be added
   */
  _updateList: function(clearList, newElements) {

    if (clearList) {
      MeasureList.measuresList.querySelectorAll('li.measures-list__item').forEach(measureListItem => {
        measureListItem.remove();
      })
    }

    newElements.forEach((liEntry) => {
      MeasureList.measuresList.appendChild(liEntry);
    });

    MeasureList.measuresListItems = document.querySelectorAll('.measures-list__item');
  },

  /**
   * Resets all selected categories.
   * @function _resetCategories
   * @private
   */
  _resetCategories: function() {
    MeasureList.categoryItems.forEach((category) => {
      if (category.checked) {
        category.checked = false;
      }
    });

    MeasureList._createCategoryList();
  },

  /**
   * Closes the overlay
   * @function _closeOverlay
   * @private
   */
  _closeOverlay: function() {
    document.documentElement.classList.remove('modal--opened');
  },

  /**
   * Opens the overlay
   * @function _openOverlay
   * @private
   */
  _openOverlay: function() {
    document.documentElement.classList.add('modal--opened');
  },

  /**
   * Checks the current media query and returns the amount of measures list items per line.
   * @function _itemsPerLine
   * @private
   * @returns {Number} - 1 to 3 depending on the current media query
   */
  _itemsPerLine: function() {
    if (window.matchMedia('(max-width: 480px)').matches) {
      return 1;
    }

    if (window.matchMedia('(min-width: 481px) and (max-width: 899px)').matches) {
      return 2;
    }

    if (window.matchMedia('(min-width: 900px)').matches) {
      return 3;
    }
  },

  /**
   * Enables or disables the loading state depending on the provided state.
   * @function _setLoading
   * @private
   * @param {Boolean} loading - whether the loading should be set to true or false
   */
  _setLoading: function(loading) {
    if (loading) {
      MeasureList.loadingIndicator.classList.remove('hidden');
    } else {
      MeasureList.loadingIndicator.classList.add('hidden');
    }
  },

  /**
   * Initializes the component.
   * @function init
   * @public
   */
  init: function() {
    MeasureList._cacheElements();
    MeasureList._initAutoComplete();
    MeasureList._bindEvents();
  }
}

if (document.querySelector('.measures-list')) {
  MeasureList.init();
}
